/**
 * ComplianceShell — domain frame for Person-Modal compliance & capture config.
 *
 * Houses the four admin editors built for the Person Modal deep-clean (Slot
 * TL). All four drive the docs-first capture + CIP slot-machine + conflict-
 * detection + gating behavior of the Person Detail Workspace:
 *
 *   - CIP ID Priority      → /v3/config/cip-id-priority      (slot-machine ranking)
 *   - Conflict Rules       → /v3/config/conflict-rules       (the 40-check menu)
 *   - Document Intelligence→ SystemSettings nra.scan.modelMap.* (DI model bindings)
 *   - Gate Enforcement     → EntityRuleGateEnforcement (ID-expiry / LexisNexis)
 *
 * Mirrors ReferenceShell / SystemShell exactly: a DomainShell with a role-
 * gated sub-nav. Lives at /compliance — wiring the previously-disabled
 * "Compliance" tile in AdminHome ("Gates, gate keys, rule applications,
 * procedure citations, deferred tracking").
 */

import { useAuth } from '@/contexts/AuthContext';
import { DomainShell } from '@/components/DomainShell';

const REQ = ['SuperAdmin', 'Admin', 'RiskManager'] as const;

const SUBNAV = [
  { slug: 'cip-id-priority', label: 'CIP ID Priority', requiredRoles: REQ },
  { slug: 'conflict-rules', label: 'Conflict Rules', requiredRoles: REQ },
  { slug: 'market-area-zips', label: 'Market-Area ZIPs', requiredRoles: REQ },
  { slug: 'document-intelligence', label: 'Document Intelligence', requiredRoles: REQ },
  { slug: 'gate-enforcement', label: 'Gate Enforcement', requiredRoles: REQ },
  { slug: 'compliance-gates', label: 'Compliance Gates', requiredRoles: REQ },
  { slug: 'gate-keys', label: 'Gate Keys', requiredRoles: REQ },
  { slug: 'entity-rules', label: 'Entity Rules', requiredRoles: REQ },
  { slug: 'procedure-citations', label: 'Procedure Citations', requiredRoles: REQ },
  { slug: 'deferred-gate-tracking', label: 'Deferred Gate Tracking', requiredRoles: REQ },
  { slug: 'lexisnexis-config', label: 'LexisNexis Config', requiredRoles: REQ },
  { slug: 'lexisnexis-call-log', label: 'LexisNexis Call Log', requiredRoles: REQ },
  { slug: 'pep-settings', label: 'PEP Settings', requiredRoles: REQ },
];

export function ComplianceShell(): JSX.Element {
  const { user } = useAuth();
  return (
    <DomainShell
      title="Compliance & Capture"
      description="The gating, CIP slot-machine, conflict-detection, and document-intelligence config behind the Person Modal. These rows decide which ID becomes CIP ID1, which cross-document discrepancies alert vs. let the officer pick, which Azure DI model reads each document, and how the ID-expiry / LexisNexis gates enforce. Defaults trace to legacy procedure; changes ripple into every account-open flow."
      basePath="/retail/compliance"
      items={SUBNAV.map((sub) => ({
        slug: sub.slug,
        label: sub.label,
        requiredRoles: sub.requiredRoles,
      }))}
      userRoles={user?.roles ?? []}
    />
  );
}
